/**
 * ImageCropDialog · Sprint D · #9
 *
 * Modal de recorte + redimensionado vanilla (sin libs externas) para imágenes
 * de logo, portada y galería. Mantiene aspect ratio configurable, pellizco
 * para zoom en móvil, drag para reposicionar, y exporta JPEG/PNG redimensionado
 * a tamaño máximo configurable (por defecto 1600px lado largo).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Crop, ArrowsClockwise, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const PREVIEW_PX = 320;        // tamaño del lienzo de previsualización (CSS px)
const OUTPUT_MAX_PX = 1600;    // lado largo máximo del JPEG resultante

export default function ImageCropDialog({ open, src, aspectRatio = 1, title = "Recortar imagen", outputType = "image/jpeg", quality = 0.86, onConfirm, onCancel }) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const dragRef = useRef({ active: false, lastX: 0, lastY: 0, pinchStart: 0, scaleStart: 1 });

  // Dimensiones del lienzo preview según aspect ratio
  const previewW = aspectRatio >= 1 ? PREVIEW_PX : Math.round(PREVIEW_PX * aspectRatio);
  const previewH = aspectRatio >= 1 ? Math.round(PREVIEW_PX / aspectRatio) : PREVIEW_PX;

  // Carga imagen
  useEffect(() => {
    if (!src || !open) {
      setImgLoaded(false);
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      imageRef.current = img;
      // Encaja inicialmente para que cubra todo el preview ("cover")
      const ratioImg = img.width / img.height;
      const ratioBox = previewW / previewH;
      const initialScale = ratioImg > ratioBox ? previewH / img.height : previewW / img.width;
      setScale(initialScale);
      setOffsetX(0);
      setOffsetY(0);
      setImgLoaded(true);
    };
    img.onerror = () => toast.error("No se pudo cargar la imagen");
    img.src = src;
  }, [src, open, previewW, previewH]);

  // Render preview
  const drawPreview = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv || !imgLoaded || !imageRef.current) return;
    const ctx = cv.getContext("2d");
    cv.width = previewW;
    cv.height = previewH;
    ctx.fillStyle = "#0f0f0f";
    ctx.fillRect(0, 0, previewW, previewH);
    const img = imageRef.current;
    const dw = img.width * scale;
    const dh = img.height * scale;
    const dx = (previewW - dw) / 2 + offsetX;
    const dy = (previewH - dh) / 2 + offsetY;
    ctx.drawImage(img, dx, dy, dw, dh);
    // Grid overlay (regla de tercios)
    ctx.strokeStyle = "rgba(255,255,255,0.18)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 3; i++) {
      ctx.beginPath(); ctx.moveTo((previewW / 3) * i, 0); ctx.lineTo((previewW / 3) * i, previewH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, (previewH / 3) * i); ctx.lineTo(previewW, (previewH / 3) * i); ctx.stroke();
    }
  }, [imgLoaded, scale, offsetX, offsetY, previewW, previewH]);

  useEffect(() => { drawPreview(); }, [drawPreview]);

  // Interacciones mouse / touch
  const onMouseDown = (e) => {
    dragRef.current.active = true;
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.active) return;
    setOffsetX((o) => o + (e.clientX - dragRef.current.lastX));
    setOffsetY((o) => o + (e.clientY - dragRef.current.lastY));
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;
  };
  const onMouseUp = () => { dragRef.current.active = false; };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      dragRef.current.pinchStart = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
      dragRef.current.scaleStart = scale;
    } else if (e.touches.length === 1) {
      dragRef.current.active = true;
      dragRef.current.lastX = e.touches[0].clientX;
      dragRef.current.lastY = e.touches[0].clientY;
    }
  };
  const onTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 2 && dragRef.current.pinchStart > 0) {
      const [a, b] = e.touches;
      const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
      const ns = dragRef.current.scaleStart * (dist / dragRef.current.pinchStart);
      setScale(Math.max(0.1, Math.min(8, ns)));
    } else if (e.touches.length === 1 && dragRef.current.active) {
      setOffsetX((o) => o + (e.touches[0].clientX - dragRef.current.lastX));
      setOffsetY((o) => o + (e.touches[0].clientY - dragRef.current.lastY));
      dragRef.current.lastX = e.touches[0].clientX;
      dragRef.current.lastY = e.touches[0].clientY;
    }
  };
  const onTouchEnd = () => {
    dragRef.current.active = false;
    dragRef.current.pinchStart = 0;
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.94 : 1.06;
    setScale((s) => Math.max(0.1, Math.min(8, s * delta)));
  };

  const reset = () => {
    if (!imageRef.current) return;
    const img = imageRef.current;
    const ratioImg = img.width / img.height;
    const ratioBox = previewW / previewH;
    const initialScale = ratioImg > ratioBox ? previewH / img.height : previewW / img.width;
    setScale(initialScale);
    setOffsetX(0);
    setOffsetY(0);
  };

  const confirm = () => {
    if (!imageRef.current) return;
    // Render a un canvas final del tamaño de salida (mismo aspect que preview)
    const ratio = aspectRatio;
    let outW, outH;
    if (ratio >= 1) {
      outW = OUTPUT_MAX_PX;
      outH = Math.round(OUTPUT_MAX_PX / ratio);
    } else {
      outH = OUTPUT_MAX_PX;
      outW = Math.round(OUTPUT_MAX_PX * ratio);
    }
    const out = document.createElement("canvas");
    out.width = outW;
    out.height = outH;
    const ctx = out.getContext("2d");
    if (outputType === "image/jpeg") {
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, outW, outH);
    }
    // Reproduce el preview a escala (previewW → outW)
    const k = outW / previewW;
    const img = imageRef.current;
    const dw = img.width * scale * k;
    const dh = img.height * scale * k;
    const dx = (outW - dw) / 2 + offsetX * k;
    const dy = (outH - dh) / 2 + offsetY * k;
    ctx.drawImage(img, dx, dy, dw, dh);
    const dataUrl = out.toDataURL(outputType, quality);
    onConfirm?.(dataUrl);
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel?.()}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="image-crop-dialog">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Crop size={18} weight="bold" className="text-[var(--ag-gold)]" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 py-3">
          <div className="text-[11px] text-gray-500 mb-2 text-center">
            Arrastra para mover · Rueda o pellizca para zoom · {aspectRatio === 1 ? "Cuadrado 1:1" : aspectRatio > 1 ? "Horizontal" : "Vertical"}
          </div>
          <div className="flex justify-center">
            <canvas
              ref={canvasRef}
              width={previewW}
              height={previewH}
              className="cursor-grab active:cursor-grabbing rounded border border-gray-300 touch-none select-none"
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onWheel={onWheel}
              data-testid="image-crop-canvas"
            />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 shrink-0">Zoom</span>
            <input
              type="range"
              min={0.1}
              max={5}
              step={0.01}
              value={scale}
              onChange={(e) => setScale(Number(e.target.value))}
              className="flex-1 accent-[var(--ag-gold)]"
              data-testid="image-crop-zoom"
            />
            <Button type="button" variant="ghost" size="sm" onClick={reset} className="text-[10px] gap-1" data-testid="image-crop-reset">
              <ArrowsClockwise size={12} /> Reset
            </Button>
          </div>
        </div>
        <DialogFooter className="px-5 pb-5 gap-2">
          <Button type="button" variant="outline" onClick={onCancel} data-testid="image-crop-cancel">
            <X size={14} className="mr-1" /> Cancelar
          </Button>
          <Button type="button" onClick={confirm} className="ag-btn-gold" data-testid="image-crop-confirm">
            <Crop size={14} className="mr-1" weight="bold" /> Recortar y subir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
